import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Search, Filter, X, Calendar, FileText, User, Clock, Tag,
  CheckCircle, AlertTriangle, Bookmark, Zap, MapPin, Globe
} from 'lucide-react';

// Document statuses
const STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-slate-500' },
  { value: 'pending_review', label: 'Pending Review', color: 'bg-amber-500' },
  { value: 'in_review', label: 'In Review', color: 'bg-blue-500' },
  { value: 'approved', label: 'Approved', color: 'bg-green-500' },
  { value: 'rejected', label: 'Rejected', color: 'bg-red-500' },
  { value: 'archived', label: 'Archived', color: 'bg-purple-500' }
];

// Quick filter presets
const QUICK_FILTERS = [
  { id: 'pending', name: 'Pending Actions', icon: Clock, filter: { status: ['pending_review', 'in_review'] } },
  { id: 'approved', name: 'Approved Docs', icon: CheckCircle, filter: { status: ['approved'] } },
  { id: 'recent', name: 'Last 7 Days', icon: Calendar, filter: { dateRange: '7' } },
  { id: 'unsigned', name: 'Needs Signature', icon: AlertTriangle, filter: { unsigned: true } },
  { id: 'my_docs', name: 'My Uploads', icon: User, filter: { myUploads: true } }
];

export default function SmartSearchPanel({
  open,
  onClose,
  onApplyFilters,
  documents = [],
  currentUser,
  documentTypes = [],
  availableSites = [],
  availableCountries = []
}) {
  // Filter states
  const [searchText, setSearchText] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [dateRange, setDateRange] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [uploaderFilter, setUploaderFilter] = useState('all');
  const [signatureFilter, setSignatureFilter] = useState('all');
  const [savedFilters, setSavedFilters] = useState([]);
  const [activeQuickFilter, setActiveQuickFilter] = useState(null);

  // Load saved filters from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('docudefender_saved_filters');
    if (saved) {
      try {
        setSavedFilters(JSON.parse(saved));
      } catch (e) {
        console.log('Error loading saved filters');
      }
    }
  }, []);

  // Apply filters to documents
  const applyFiltersToDocuments = useCallback(() => {
    let filtered = [...documents];

    // Text search
    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.name?.toLowerCase().includes(search) ||
        doc.document_type?.toLowerCase().includes(search) ||
        doc.artifact_classification?.toLowerCase().includes(search) ||
        doc.description?.toLowerCase().includes(search)
      );
    }

    // Status filter
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter(doc => selectedStatuses.includes(doc.status));
    }

    // Document type filter
    if (selectedTypes.length > 0) {
      filtered = filtered.filter(doc => selectedTypes.includes(doc.document_type));
    }

    // Date range filter
    if (dateRange !== 'all') {
      const now = new Date();
      let cutoffDate = new Date();
      
      switch (dateRange) {
        case '7':
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case '14':
          cutoffDate.setDate(now.getDate() - 14);
          break;
        case '30':
          cutoffDate.setDate(now.getDate() - 30);
          break;
        case '90':
          cutoffDate.setDate(now.getDate() - 90);
          break;
        case 'custom':
          if (dateFrom) cutoffDate = new Date(dateFrom);
          break;
        default:
          break;
      }

      filtered = filtered.filter(doc => {
        if (!doc.created_at) return false;
        const docDate = new Date(doc.created_at);
        if (dateRange === 'custom' && dateTo) {
          return docDate >= cutoffDate && docDate <= new Date(dateTo);
        }
        return docDate >= cutoffDate;
      });
    }

    // Uploader filter
    if (uploaderFilter === 'mine' && currentUser) {
      filtered = filtered.filter(doc => doc.uploaded_by === currentUser.id);
    }

    // Signature filter
    if (signatureFilter === 'unsigned') {
      filtered = filtered.filter(doc => 
        !doc.signatures || doc.signatures.length === 0
      );
    } else if (signatureFilter === 'signed') {
      filtered = filtered.filter(doc => 
        doc.signatures && doc.signatures.length > 0
      );
    } else if (signatureFilter === 'needs_my_signature') {
      filtered = filtered.filter(doc => {
        const assignment = doc.assignees_with_roles?.find(
          a => a.email?.toLowerCase() === currentUser?.email?.toLowerCase()
        );
        return assignment?.signature_required && 
          !doc.signatures?.some(s => s.signer_email?.toLowerCase() === currentUser?.email?.toLowerCase());
      });
    }

    // Site filter
    if (selectedSites.length > 0) {
      filtered = filtered.filter(doc => {
        if (!doc.sites || doc.sites.length === 0) return false;
        return doc.sites.some(site => 
          selectedSites.includes(site.site_id) || selectedSites.includes(site.site_code)
        );
      });
    }

    // Country filter
    if (selectedCountries.length > 0) {
      filtered = filtered.filter(doc => {
        if (!doc.countries || doc.countries.length === 0) return false;
        return doc.countries.some(country => selectedCountries.includes(country));
      });
    }

    return filtered;
  }, [documents, searchText, selectedStatuses, selectedTypes, selectedSites, selectedCountries, dateRange, dateFrom, dateTo, uploaderFilter, signatureFilter, currentUser]);

  // Calculate result count using memoization
  const resultCount = useMemo(() => {
    return applyFiltersToDocuments().length;
  }, [applyFiltersToDocuments]);

  const handleApplyFilters = () => {
    const filtered = applyFiltersToDocuments();
    onApplyFilters(filtered, {
      searchText,
      selectedStatuses,
      selectedTypes,
      selectedSites,
      selectedCountries,
      dateRange,
      dateFrom,
      dateTo,
      uploaderFilter,
      signatureFilter
    });
    onClose();
  };

  const handleQuickFilter = (preset) => {
    // Reset all filters first
    setSearchText('');
    setSelectedStatuses([]);
    setSelectedTypes([]);
    setSelectedSites([]);
    setSelectedCountries([]);
    setDateRange('all');
    setDateFrom('');
    setDateTo('');
    setUploaderFilter('all');
    setSignatureFilter('all');
    
    setActiveQuickFilter(preset.id);

    // Apply preset filters
    if (preset.filter.status) {
      setSelectedStatuses(preset.filter.status);
    }
    if (preset.filter.dateRange) {
      setDateRange(preset.filter.dateRange);
    }
    if (preset.filter.unsigned) {
      setSignatureFilter('unsigned');
    }
    if (preset.filter.myUploads) {
      setUploaderFilter('mine');
    }
  };

  const resetFilters = () => {
    setSearchText('');
    setSelectedStatuses([]);
    setSelectedTypes([]);
    setSelectedSites([]);
    setSelectedCountries([]);
    setDateRange('all');
    setDateFrom('');
    setDateTo('');
    setUploaderFilter('all');
    setSignatureFilter('all');
    setActiveQuickFilter(null);
  };

  const saveCurrentFilter = () => {
    const filterName = prompt('Enter a name for this filter:');
    if (!filterName) return;

    const newFilter = {
      id: Date.now().toString(),
      name: filterName,
      filter: {
        searchText,
        selectedStatuses,
        selectedTypes,
        selectedSites,
        selectedCountries,
        dateRange,
        dateFrom,
        dateTo,
        uploaderFilter,
        signatureFilter
      }
    };

    const updated = [...savedFilters, newFilter];
    setSavedFilters(updated);
    localStorage.setItem('docudefender_saved_filters', JSON.stringify(updated));
  };

  const loadSavedFilter = (filter) => {
    setSearchText(filter.filter.searchText || '');
    setSelectedStatuses(filter.filter.selectedStatuses || []);
    setSelectedTypes(filter.filter.selectedTypes || []);
    setSelectedSites(filter.filter.selectedSites || []);
    setSelectedCountries(filter.filter.selectedCountries || []);
    setDateRange(filter.filter.dateRange || 'all');
    setDateFrom(filter.filter.dateFrom || '');
    setDateTo(filter.filter.dateTo || '');
    setUploaderFilter(filter.filter.uploaderFilter || 'all');
    setSignatureFilter(filter.filter.signatureFilter || 'all');
    setActiveQuickFilter(null);
  };

  const deleteSavedFilter = (filterId) => {
    const updated = savedFilters.filter(f => f.id !== filterId);
    setSavedFilters(updated);
    localStorage.setItem('docudefender_saved_filters', JSON.stringify(updated));
  };

  const toggleStatus = (status) => {
    setSelectedStatuses(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
    setActiveQuickFilter(null);
  };

  const toggleType = (type) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const toggleSite = (siteId) => {
    setSelectedSites(prev =>
      prev.includes(siteId)
        ? prev.filter(s => s !== siteId)
        : [...prev, siteId]
    );
    setActiveQuickFilter(null);
  };

  const toggleCountry = (country) => {
    setSelectedCountries(prev =>
      prev.includes(country)
        ? prev.filter(c => c !== country)
        : [...prev, country]
    );
    setActiveQuickFilter(null);
  };

  // Get unique document types from documents
  const uniqueTypes = useMemo(() => {
    return [...new Set(documents.map(d => d.document_type).filter(Boolean))];
  }, [documents]);

  // Extract unique sites and countries from documents if not provided via props
  const extractedSites = useMemo(() => {
    if (availableSites.length > 0) return availableSites;
    const sites = new Map();
    documents.forEach(doc => {
      doc.sites?.forEach(site => {
        if (site.site_id && !sites.has(site.site_id)) {
          sites.set(site.site_id, site);
        }
      });
    });
    return Array.from(sites.values());
  }, [documents, availableSites]);

  const extractedCountries = useMemo(() => {
    if (availableCountries.length > 0) return availableCountries;
    const countries = new Set();
    documents.forEach(doc => {
      doc.countries?.forEach(country => countries.add(country));
    });
    return Array.from(countries);
  }, [documents, availableCountries]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-white">
            <Filter className="w-5 h-5 text-red-600" />
            Smart Search & Filters
            <Badge className="ml-2 bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/30">
              {documents.length} total documents
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Quick Filters */}
          <div>
            <Label className="text-slate-700 dark:text-slate-200 text-sm font-medium mb-2 block">Quick Filters</Label>
            <div className="flex flex-wrap gap-2">
              {QUICK_FILTERS.map((preset) => {
                const Icon = preset.icon;
                return (
                  <Button
                    key={preset.id}
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuickFilter(preset)}
                    className={`${
                      activeQuickFilter === preset.id
                        ? 'bg-red-600 border-red-500 text-white'
                        : 'bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {preset.name}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Search Text */}
          <div>
            <Label className="text-slate-700 dark:text-slate-200 text-sm font-medium mb-2 block">Search Documents</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-400" />
              <Input
                value={searchText}
                onChange={(e) => { setSearchText(e.target.value); setActiveQuickFilter(null); }}
                placeholder="Search by name, type, description..."
                className="pl-10 bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <Label className="text-slate-700 dark:text-slate-200 text-sm font-medium mb-2 block">Status</Label>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((status) => (
                <Button
                  key={status.value}
                  variant="outline"
                  size="sm"
                  onClick={() => toggleStatus(status.value)}
                  className={`${
                    selectedStatuses.includes(status.value)
                      ? 'bg-red-600 border-red-500 text-white'
                      : 'bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${status.color} mr-2`}></span>
                  {status.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-700 dark:text-slate-200 text-sm font-medium mb-2 block">Date Range</Label>
              <Select value={dateRange} onValueChange={(v) => { setDateRange(v); setActiveQuickFilter(null); }}>
                <SelectTrigger className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                  <SelectItem value="all" className="text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700">All Time</SelectItem>
                  <SelectItem value="7" className="text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700">Last 7 Days</SelectItem>
                  <SelectItem value="14" className="text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700">Last 14 Days</SelectItem>
                  <SelectItem value="30" className="text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700">Last 30 Days</SelectItem>
                  <SelectItem value="90" className="text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700">Last 90 Days</SelectItem>
                  <SelectItem value="custom" className="text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {dateRange === 'custom' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-slate-600 dark:text-slate-300 text-xs">From</Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <Label className="text-slate-600 dark:text-slate-300 text-xs">To</Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Document Type Filter */}
          {uniqueTypes.length > 0 && (
            <div>
              <Label className="text-slate-700 dark:text-slate-200 text-sm font-medium mb-2 block">Document Type ({uniqueTypes.length} types)</Label>
              <ScrollArea className="h-24 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-2">
                <div className="flex flex-wrap gap-2">
                  {uniqueTypes.map((type) => (
                    <Button
                      key={type}
                      variant="outline"
                      size="sm"
                      onClick={() => toggleType(type)}
                      className={`text-xs ${
                        selectedTypes.includes(type)
                          ? 'bg-red-600 border-red-500 text-white'
                          : 'bg-slate-100 dark:bg-slate-600 border-slate-300 dark:border-slate-500 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-500'
                      }`}
                    >
                      <Tag className="w-3 h-3 mr-1" />
                      {type}
                    </Button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Site and Country Filters - NEW */}
          <div className="grid grid-cols-2 gap-4">
            {/* Site Filter */}
            {extractedSites.length > 0 && (
              <div>
                <Label className="text-slate-700 dark:text-slate-200 text-sm font-medium mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-teal-500" />
                  Filter by Site ({extractedSites.length})
                </Label>
                <ScrollArea className="h-20 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-2">
                  <div className="flex flex-wrap gap-1">
                    {extractedSites.map((site) => (
                      <Button
                        key={site.site_id}
                        variant="outline"
                        size="sm"
                        onClick={() => toggleSite(site.site_id || site.site_code)}
                        className={`text-xs h-7 ${
                          selectedSites.includes(site.site_id) || selectedSites.includes(site.site_code)
                            ? 'bg-teal-600 border-teal-500 text-white'
                            : 'bg-slate-100 dark:bg-slate-600 border-slate-300 dark:border-slate-500 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-500'
                        }`}
                      >
                        {site.site_code || site.site_name}
                      </Button>
                    ))}
                  </div>
                </ScrollArea>
                {selectedSites.length > 0 && (
                  <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">{selectedSites.length} site(s) selected</p>
                )}
              </div>
            )}

            {/* Country Filter */}
            {extractedCountries.length > 0 && (
              <div>
                <Label className="text-slate-700 dark:text-slate-200 text-sm font-medium mb-2 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-blue-500" />
                  Filter by Country ({extractedCountries.length})
                </Label>
                <ScrollArea className="h-20 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-2">
                  <div className="flex flex-wrap gap-1">
                    {extractedCountries.map((country) => (
                      <Button
                        key={country}
                        variant="outline"
                        size="sm"
                        onClick={() => toggleCountry(country)}
                        className={`text-xs h-7 ${
                          selectedCountries.includes(country)
                            ? 'bg-blue-600 border-blue-500 text-white'
                            : 'bg-slate-100 dark:bg-slate-600 border-slate-300 dark:border-slate-500 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-500'
                        }`}
                      >
                        {country}
                      </Button>
                    ))}
                  </div>
                </ScrollArea>
                {selectedCountries.length > 0 && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">{selectedCountries.length} country/countries selected</p>
                )}
              </div>
            )}
          </div>

          {/* Advanced Filters */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-700 dark:text-slate-200 text-sm font-medium mb-2 block">Uploaded By</Label>
              <Select value={uploaderFilter} onValueChange={(v) => { setUploaderFilter(v); setActiveQuickFilter(null); }}>
                <SelectTrigger className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                  <SelectItem value="all" className="text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700">All Users</SelectItem>
                  <SelectItem value="mine" className="text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700">My Uploads Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-700 dark:text-slate-200 text-sm font-medium mb-2 block">Signature Status</Label>
              <Select value={signatureFilter} onValueChange={(v) => { setSignatureFilter(v); setActiveQuickFilter(null); }}>
                <SelectTrigger className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600">
                  <SelectItem value="all" className="text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700">All Documents</SelectItem>
                  <SelectItem value="unsigned" className="text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700">Unsigned</SelectItem>
                  <SelectItem value="signed" className="text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700">Has Signatures</SelectItem>
                  <SelectItem value="needs_my_signature" className="text-slate-900 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700">Needs My Signature</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Saved Filters */}
          {savedFilters.length > 0 && (
            <div>
              <Label className="text-slate-700 dark:text-slate-200 text-sm mb-2 block">Saved Filters</Label>
              <div className="flex flex-wrap gap-2">
                {savedFilters.map((filter) => (
                  <div key={filter.id} className="flex items-center gap-1 bg-slate-100 dark:bg-slate-700 rounded-lg border border-slate-300 dark:border-slate-600 pl-3 pr-1 py-1">
                    <button
                      onClick={() => loadSavedFilter(filter)}
                      className="text-sm text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
                    >
                      <Bookmark className="w-3 h-3 inline mr-1" />
                      {filter.name}
                    </button>
                    <button
                      onClick={() => deleteSavedFilter(filter.id)}
                      className="p-1 text-slate-500 dark:text-slate-400 hover:text-red-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Result Count */}
          <div className="flex items-center justify-between p-3 bg-slate-100/80 dark:bg-slate-700/50 rounded-lg border border-slate-300 dark:border-slate-600">
            <span className="text-slate-700 dark:text-slate-200">
              <Zap className="w-4 h-4 inline mr-2 text-amber-400" />
              <span className="font-semibold text-slate-900 dark:text-white">{resultCount}</span> of {documents.length} documents match your filters
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={saveCurrentFilter}
              className="text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
            >
              <Bookmark className="w-4 h-4 mr-1" />
              Save Filter
            </Button>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={resetFilters}
            className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-600 hover:text-slate-900 dark:hover:text-white"
          >
            <X className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button
            onClick={handleApplyFilters}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Filter className="w-4 h-4 mr-2" />
            Apply Filters ({resultCount})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
